/**
 * Wordlist bulk-mark modal — web port of iOS
 * `client-native/.../feed/components/FeedWordlistModal.tsx` (PostHog flag
 * `wordlist_in_feed`).
 *
 * iOS selection model: every filtered word starts SELECTED and the user
 * deselects the ones they don't know. Submit marks selected words "known" AND
 * deselected words "learning". Only "new" words show by default, with a
 * "Show learning words" toggle. Light `Colors.Background` sheet, chips show
 * targetWord + nativeWord, success plays the correct sound, then a count-up
 * celebration card auto-closes after 3s.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import {
  getWordFrequencyTier,
  TIER_ORDER,
  TIER_LABELS,
  type FrequencyTier,
} from "@/data/spanishWordFrequency";
import { callWithAuth, putWordStatusBulk } from "@/api/Api";
import { useDictionaryStats } from "@/hooks/useDictionaryStats";
import { useSound } from "@/hooks/useSound";
import { colors, FlagIcon } from "@/ui";
import type { UserVideoWord, UserWordStatus } from "../types";
import { peekWordOverride, setWordOverride } from "../state/videoState";
import { cancelPendingLearningWrite } from "../lib/wordStatus";
import { useTrackFeedEvents } from "../hooks/useTrackFeedEvents";

const CELEBRATION_AUTO_CLOSE_MS = 3000;

/** iOS lesson/lib/utils.ts isInvalidDictionaryWord — numbers/punctuation only. */
function isInvalidDictionaryWord(displayText: string): boolean {
  return /^[\d\s\p{P}]+$/u.test(displayText.trim());
}

/** iOS lesson/lib/utils.ts filterForUniqueWordKeys — keep first per wordKey. */
function filterForUniqueWordKeys(words: UserVideoWord[]): UserVideoWord[] {
  return words.filter(
    (word, index, self) =>
      self.findIndex((w) => w.wordKey === word.wordKey) === index,
  );
}

/** Animated count-up number (iOS AnimatedCount: 300ms delay, 800ms ease-out). */
function AnimatedCount({ from, to }: { from: number; to: number }) {
  const [display, setDisplay] = useState(from);
  useEffect(() => {
    let raf = 0;
    const delay = 300;
    const duration = 800;
    const start = performance.now() + delay;
    const tick = (now: number) => {
      const t = Math.min(1, Math.max(0, (now - start) / duration));
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setDisplay(Math.round(from + (to - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to]);
  return (
    <p className="text-4xl font-bold text-center" style={{ color: colors.primary }}>
      {display}
    </p>
  );
}

/** Celebration overlay — covers the scroll area (iOS CelebrationOverlay). */
function CelebrationOverlay({
  data,
  onDismiss,
}: {
  data: { prevKnown: number; newKnown: number; knownAdded: number };
  onDismiss: () => void;
}) {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  useEffect(() => {
    const timer = setTimeout(() => onDismissRef.current(), CELEBRATION_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center cursor-pointer"
      style={{ backgroundColor: colors.background }}
      onClick={onDismiss}
    >
      <div className="relative">
        {data.knownAdded > 0 && (
          <div
            className="absolute -top-2.5 -right-2.5 z-10 px-2 py-0.5 rounded-[14px] shadow-md"
            style={{ backgroundColor: colors.green }}
          >
            <span className="text-white text-sm font-bold">+{data.knownAdded}</span>
          </div>
        )}
        <div
          className="w-40 flex flex-col items-center gap-1 rounded-2xl border px-4 py-5 shadow-sm"
          style={{ backgroundColor: colors.white, borderColor: colors.greyLight }}
        >
          <p className="text-xs text-center flex items-center justify-center gap-1" style={{ color: colors.muted }}>
            <FlagIcon countryCode="ES" size="small" /> Known Words
          </p>
          <AnimatedCount from={data.prevKnown} to={data.newKnown} />
        </div>
      </div>
    </div>
  );
}

interface FeedWordlistModalProps {
  lessonId: number;
  words: UserVideoWord[];
  onClose: () => void;
  /**
   * Fired when the modal resolves after a SUCCESSFUL bulk-mark (celebration
   * auto-close or tap-dismiss) — distinct from a plain close. Mirrors iOS
   * FeedWordlistModal onComplete.
   */
  onComplete?: () => void;
}

export function FeedWordlistModal({
  lessonId,
  words,
  onClose,
  onComplete,
}: FeedWordlistModalProps) {
  const { trackEvent } = useTrackFeedEvents();
  const { knownWordCount, addKnownWords, addLearningWords } = useDictionaryStats();
  const { playCorrectSound } = useSound();
  // iOS model: every filtered word starts selected; the user DEselects.
  const [deselectedWordKeys, setDeselectedWordKeys] = useState<Set<string>>(
    new Set(),
  );
  const [showLearningWords, setShowLearningWords] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [celebrationData, setCelebrationData] = useState<{
    prevKnown: number;
    newKnown: number;
    knownAdded: number;
  } | null>(null);

  useEffect(() => {
    trackEvent("feed_wordlist_opened", { lessonId });
  }, [trackEvent, lessonId]);

  // Filter words exactly like iOS: unique by wordKey, valid dictionary words,
  // never known; "new" always, "learning" only when the toggle is on.
  const filteredWords = useMemo(() => {
    return filterForUniqueWordKeys(words).filter((word) => {
      if (isInvalidDictionaryWord(word.displayText)) return false;
      const effectiveStatus = peekWordOverride(word.wordKey) ?? word.status;
      if (effectiveStatus === "known") return false;
      if (effectiveStatus === "new") return true;
      if (showLearningWords && effectiveStatus === "learning") return true;
      return false;
    });
  }, [words, showLearningWords]);

  const groupedWords = useMemo(() => {
    const byTier = new Map<FrequencyTier, UserVideoWord[]>();
    for (const word of filteredWords) {
      const tier = getWordFrequencyTier(word.wordKey);
      const list = byTier.get(tier) ?? [];
      list.push(word);
      byTier.set(tier, list);
    }
    return byTier;
  }, [filteredWords]);

  const selectedCount = filteredWords.filter(
    (w) => !deselectedWordKeys.has(w.wordKey),
  ).length;

  const toggleWord = (wordKey: string) =>
    setDeselectedWordKeys((prev) => {
      const next = new Set(prev);
      if (next.has(wordKey)) next.delete(wordKey);
      else next.add(wordKey);
      return next;
    });

  const toggleTier = (tier: FrequencyTier) => {
    const tierWords = groupedWords.get(tier) ?? [];
    const allSelected = tierWords.every((w) => !deselectedWordKeys.has(w.wordKey));
    setDeselectedWordKeys((prev) => {
      const next = new Set(prev);
      tierWords.forEach((w) =>
        allSelected ? next.add(w.wordKey) : next.delete(w.wordKey),
      );
      return next;
    });
  };

  const handleSubmit = async () => {
    if (filteredWords.length === 0) {
      onClose();
      return;
    }
    if (submitting || celebrationData) return;
    setSubmitting(true);

    playCorrectSound();

    const prevKnown = knownWordCount;
    // Selected → known, deselected → learning (iOS handleSubmit).
    const knownWords = filteredWords.filter((w) => !deselectedWordKeys.has(w.wordKey));
    const learningWords = filteredWords.filter((w) => deselectedWordKeys.has(w.wordKey));
    const previousStatuses = new Map<string, UserWordStatus>();
    const applyOverride = (word: UserVideoWord, status: UserWordStatus) => {
      // Cancel any deferred first-tap "learning" write for this word so it can't
      // fire after this bulk-mark and overwrite it on the server.
      cancelPendingLearningWrite(word.wordKey);
      previousStatuses.set(
        word.wordKey,
        peekWordOverride(word.wordKey) ?? word.status,
      );
      setWordOverride(word.wordKey, status);
    };
    knownWords.forEach((w) => applyOverride(w, "known"));
    learningWords.forEach((w) => applyOverride(w, "learning"));

    const toPairs = (list: UserVideoWord[]) =>
      list.map((w) => ({
        wordKey: w.wordKey,
        definitionId: w.definition.definitionId,
      }));

    try {
      if (knownWords.length > 0) {
        await callWithAuth(putWordStatusBulk, {
          wordKeyDefinitionIDPairs: toPairs(knownWords),
          newStatus: "known",
          lessonId,
        });
      }
      if (learningWords.length > 0) {
        await callWithAuth(putWordStatusBulk, {
          wordKeyDefinitionIDPairs: toPairs(learningWords),
          newStatus: "learning",
          lessonId,
        });
      }
      addKnownWords(knownWords.length);
      addLearningWords(learningWords.length);
      trackEvent("feed_wordlist_marked_known", {
        lessonId,
        count: knownWords.length,
      });
      setCelebrationData({
        prevKnown,
        newKnown: prevKnown + knownWords.length,
        knownAdded: knownWords.length,
      });
    } catch {
      // Revert optimistic overrides on failure.
      previousStatuses.forEach((status, wordKey) => {
        setWordOverride(wordKey, status);
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Portal to <body>: the modal is rendered deep inside the feed, whose wrapper
  // shares the NavBar's `z-10` stacking level (HomeLayout). A nested `z-[60]`
  // can't escape that context, so the persistent bottom NavBar painted OVER the
  // sheet (its footer button sat behind the nav). Portaling lifts the overlay to
  // the root stacking context so it covers the NavBar like an iOS sheet.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-[440px] max-h-[80dvh] flex flex-col rounded-t-[20px] overflow-hidden pt-4 pb-4"
        style={{ backgroundColor: colors.background }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-2">
          <div>
            <p className="text-xl font-bold" style={{ color: colors.primary }}>
              Words
            </p>
            <p className="text-xs" style={{ color: colors.muted }}>
              Select the words you already know
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 flex items-center justify-center"
            style={{ color: colors.grey }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toggle for learning words */}
        <div
          className="flex items-center justify-between px-5 py-2 border-b"
          style={{ borderColor: colors.greyLight }}
        >
          <span className="text-base" style={{ color: colors.muted }}>
            Show learning words
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={showLearningWords}
            onClick={() => setShowLearningWords((v) => !v)}
            className="relative w-12 h-7 rounded-full transition-colors duration-200"
            style={{
              backgroundColor: showLearningWords ? colors.green : colors.greyLight,
            }}
          >
            <span
              className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-[left] duration-200"
              style={{ left: showLearningWords ? 22 : 2 }}
            />
          </button>
        </div>

        {/* Word sections — with celebration overlay on top. The scroll area gets
            an EXPLICIT max-height (sheet 80dvh minus the header/toggle/footer
            chrome ≈ 200px) rather than relying on flex `flex-1`/`h-full`: that
            combo creates a circular size that ignores the sheet's max-height, so
            the list grew past the sheet and never scrolled. With a definite max
            the list sizes to its content when short and scrolls when long. */}
        <div
          className="relative overflow-hidden"
          style={celebrationData ? { minHeight: 250 } : undefined}
        >
          <div
            className="overflow-y-auto px-4 pt-3 pb-10"
            style={{
              maxHeight: "calc(80dvh - 215px)",
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
            }}
          >
            {filteredWords.length === 0 && !celebrationData ? (
              <div className="py-10 flex justify-center">
                <p className="text-base text-center" style={{ color: colors.muted }}>
                  No new words in this lesson
                </p>
              </div>
            ) : (
              TIER_ORDER.map((tier) => {
                const tierWords = groupedWords.get(tier) ?? [];
                if (tierWords.length === 0) return null;
                const allSelected = tierWords.every(
                  (w) => !deselectedWordKeys.has(w.wordKey),
                );
                return (
                  <div key={tier} className="mb-4">
                    <div className="flex items-center justify-between px-1 mb-2">
                      <span
                        className="text-base font-semibold"
                        style={{ color: colors.primary }}
                      >
                        {TIER_LABELS[tier]}{" "}
                        <span
                          className="text-xs font-normal"
                          style={{ color: colors.muted }}
                        >
                          ({tierWords.length})
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleTier(tier)}
                        className="text-xs font-semibold"
                        style={{ color: colors.blue }}
                      >
                        {allSelected ? "Deselect all" : "Select all"}
                      </button>
                    </div>
                    <div className="flex flex-wrap">
                      {tierWords.map((word) => {
                        const selected = !deselectedWordKeys.has(word.wordKey);
                        return (
                          <button
                            key={word.wordKey}
                            type="button"
                            onClick={() => toggleWord(word.wordKey)}
                            className="flex items-center px-3 py-2 rounded-[20px] border m-1 text-left transition-colors active:scale-95"
                            style={{
                              backgroundColor: selected ? "#E8F5E9" : colors.greyLightest,
                              borderColor: selected ? colors.green : colors.greyLight,
                            }}
                          >
                            {selected && (
                              <Check
                                className="w-3 h-3 mr-1.5 shrink-0"
                                style={{ color: colors.green }}
                              />
                            )}
                            <span className="flex flex-col min-w-0">
                              <span
                                className="text-base font-semibold truncate"
                                style={{
                                  color: selected ? colors.primary : colors.muted,
                                }}
                              >
                                {word.definition.targetWord}
                              </span>
                              <span
                                className="text-xs truncate mt-px"
                                style={{ color: colors.muted }}
                              >
                                {word.definition.nativeWord}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {celebrationData && (
            <CelebrationOverlay
              data={celebrationData}
              onDismiss={() => {
                onComplete?.();
                onClose();
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div
          className="px-5 pt-3 border-t"
          style={{ borderColor: colors.greyLight }}
        >
          <button
            type="button"
            onClick={handleSubmit}
            disabled={filteredWords.length === 0 || submitting || celebrationData != null}
            className="w-full py-3.5 rounded-full text-white text-sm font-bold active:scale-[0.98] transition-transform disabled:opacity-40"
            style={{ backgroundColor: colors.blue }}
          >
            {`Mark ${selectedCount} word${selectedCount !== 1 ? "s" : ""} as known`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
